import {
  AccountApprovalStatus,
  ActionOnLimitReached,
  BugleMessageType,
  EmploymentType,
  ForwardFillMax,
  GenericAcceptShiftBypasses,
  MaxCountInDays,
  MinCountInDays,
  RankWeights,
  RequiredAttributeLevel,
  ShiftAutoFillMode,
  ShiftAutoFillType,
  TierLevel,
  Transportation,
  WorkerGender,
} from '../..'
import { ShiftAttribute } from '../shift-attributes'
import { ShiftSignupStatus } from '../shift-enums'
import { MetablockConfig } from './metablock'
import { WorkflowStepType } from './workflow-step'

/**
 * WorkerStepFilter is used to filter workers in shift readiness workflow steps
 * (e.g., announcement, robocall) based on their metrics and performance data.
 */
export type WorkerStepFilter = {
  minCompletedShifts?: string
  maxCompletedShifts?: string
  minCompletedShiftsWithCompanyInDays?: MinCountInDays
  maxCompletedShiftsWithCompanyInDays?: MaxCountInDays
  minReliabilityScore?: string
  maxReliabilityScore?: string
  tierLevels?: TierLevel[]
  minNoShowsInDays?: MinCountInDays
  maxNoShowsInDays?: MaxCountInDays
  minLateCancelsInDays?: MinCountInDays
  maxLateCancelsInDays?: MaxCountInDays
  minLateArrivalsInDays?: MinCountInDays
  maxLateArrivalsInDays?: MaxCountInDays
  minAbandonsInDays?: MinCountInDays
  maxAbandonsInDays?: MaxCountInDays
}

export type ActionStepData =
  | { type: WorkflowStepType.WAIT_DURATION; data: WaitDurationStepData }
  | { type: WorkflowStepType.BUGLE; data: BugleStepData }
  | { type: WorkflowStepType.FORWARD_FILL; data: ForwardFillStepData }
  | {
      type: WorkflowStepType.EDIT_SHIFT_CONSTRAINTS
      data: EditShiftConstraintsStepData
    }
  | { type: WorkflowStepType.SLACK; data: SlackStepData }
  | { type: WorkflowStepType.SHIFT_INVITATIONS; data: ShiftInvitationsStepData }
  | { type: WorkflowStepType.SHIFT_AUTO_FILL; data: ShiftAutoFillStepData }
  | {
      type: WorkflowStepType.SHIFT_AUTO_FILL_STOP_SESSIONS
      data: ShiftAutoFillStopSessionsStepData
    }
  | { type: WorkflowStepType.ANNOUNCEMENT; data: AnnouncementStepData }
  | {
      type: WorkflowStepType.PRE_SHIFT_CONFIRMATION
      data: PreShiftConfirmationStepData
    }
  | { type: WorkflowStepType.ROBOCALL; data: RobocallStepData }
  | { type: WorkflowStepType.EMAIL; data: EmailStepData }
  | {
      type: WorkflowStepType.LATE_CANCELLATION_CHECKPOINT
      data: LateCancellationCheckpointStepData
    }
  | { type: WorkflowStepType.AI_CONVERSATIONAL_SMS; data: AiSmsStepData }
  | {
      type: WorkflowStepType.WAIT_CALENDAR_TIME
      data: WaitCalendarTimeStepData
    }
  | { type: WorkflowStepType.METABLOCK; data: MetablockConfig }

export type WaitDurationStepData = {
  durationInMinutes: number
}

export type WaitCalendarTimeStepData = {
  daysBeforeTimestamp: number
  hour: number
  minute: number
}

export type BugleStepData = {
  message: {
    type: BugleMessageType
    title?: string
    body: string
    redirectScreen?: string
  }
  customSearch: WorkerCustomSearchParameters
  workerSearchLimit: WorkerSearchLimitConfig
}

export type ForwardFillStepData = {
  forwardFillFilters?: ForwardFillFilters
  rankedCandidateWeights?: RankWeights
}

export type ForwardFillFilters = {
  maxDistanceInMiles?: number
  transportationType?: Transportation
  canPassBGC?: boolean
  hasEngagedBGCInReview?: boolean
  hasCleanBGC?: boolean
  canPassDrugTest?: boolean
}

export type EditShiftConstraintsStepData = {
  forwardFillMax?: ForwardFillMax
  minimumAcceptedTier?: TierLevel
  requiredAttributes?: ShiftAttribute[]
  requiredAttributeLevels?: RequiredAttributeLevel[]
  unprovenWorkerThreshold?: number
  signupStatus?: ShiftSignupStatus
  paidBackup?: { payAmount: PayAmountConfig; slotsRequested: SlotsConfig }
  overbookSlotsRequested?: SlotsConfig
  enableDynamicOverbooking?: boolean
}

export enum SlackDestination {
  TEMPLATE_CHANNEL = 'TEMPLATE_CHANNEL',
  ASSIGNED_POD_CHANNEL = 'ASSIGNED_POD_CHANNEL',
  SHIFT_QUALITY_WOPS = 'SHIFT_QUALITY_WOPS',
}

export type ShiftQualityChannelMessageData = {
  slackDestination: SlackDestination.SHIFT_QUALITY_WOPS
}

export type PodSlackChannelMessageData = {
  slackDestination: SlackDestination.ASSIGNED_POD_CHANNEL
}

export type TemplateChannelMessageData = {
  slackDestination: SlackDestination.TEMPLATE_CHANNEL
  messageTemplate: string
  channel: string
}

export type SlackStepData =
  | PodSlackChannelMessageData
  | TemplateChannelMessageData
  | ShiftQualityChannelMessageData

export enum WorkerSearchLimit {
  MAX_WORKERS = 'MAX_WORKERS',
  COEFFICIENT_BASED = 'COEFFICIENT_BASED',
}

export const workerSearchLimitOptions = [
  { value: WorkerSearchLimit.MAX_WORKERS, label: 'Number of Workers' },
  {
    value: WorkerSearchLimit.COEFFICIENT_BASED,
    label: 'Multiple of # of Open Slots',
  },
]

export type WorkerSearchLimitConfig = {
  type: WorkerSearchLimit
  maxWorkers?: number
  coefficientForAvailableSlots?: number
}

export type WorkerCustomSearchParameters = {
  regionIds?: string[] | null
  isAvailableForShift?: boolean
  hasNoSameDayShifts?: boolean
  maxDistanceFromShift?: number
  hasWorkedWithCompany?: boolean
  accountStatuses?: AccountApprovalStatus[]
  workerProfileFields?: string[]
  tierLevels?: TierLevel[]
  workerGenders?: WorkerGender[]
  minReliabilityScore?: number
  isOnWaitlist?: boolean
  isOnWaitlistSameDay?: boolean
  hasEligibilityGroups?: string[]
  attributeLevels?: RequiredAttributeLevel[] | null
  isInvitedToShift?: boolean
  isFavoriteWithCompany?: boolean
  backfilledForRoleFamilyInLast14Days?: boolean
  removedDueToOverbookAndAppeasedSameDay?: boolean
  employmentTypes?: EmploymentType[]
  hasAppliedToApplicationIds?: string[]
  workerHasSpecificShifts?: {
    minCount: number
    maxCount?: number
    roleFamily?: boolean
    companyId?: boolean
  }
}

export enum ShiftInvitationType {
  SEARCH_BASED = 'SEARCH_BASED',
  ROSTER_BASED = 'ROSTER_BASED',
}

export type VirtualRosterSelection = {
  hasWorkedWithCompany?: boolean
  hasWorkedAtLocation?: boolean
  hasWorkedAsRole?: boolean
  favoriteWorkers?: boolean
}

export type RosterSelection = {
  rosterIds: string[]
  virtualRosterIds: VirtualRosterSelection
}

export type ShiftInvitationsStepData = {
  type: ShiftInvitationType
  customSearch?: WorkerCustomSearchParameters
  workerSearchLimit?: WorkerSearchLimitConfig
  selectedRosters?: RosterSelection
  bypasses?: {
    allowOverbook?: boolean
    bypassSignupStatus?: boolean
    bypassBGCRequirement?: boolean
    bypassFavoritesOnly?: boolean
    bypassFavoritesFirst?: boolean
    bypassUnprovenThreshold?: boolean
    bypassAttributeRequirements?: boolean
    bypassCertifications?: boolean
    bypassWorkerAvailability?: boolean
    bypassExtraBGCRequirement?: boolean
    bypassGenderPreference?: boolean
    bypassMinimumAgeRequirement?: boolean
    bypassResumeUploadRequired?: boolean
    bypassAccountStatus?: boolean
    bypassMinimumAcceptedTier?: boolean
    bypassShiftStatus?: boolean
    bypassInvitations?: boolean
    bypassPaidBackupSlotsFull?: boolean
    bypassForwardFillExperienced?: boolean
    bypassForwardFillInexperienced?: boolean
    bypassWorkerShiftIsNotBizCancelled?: boolean
    bypassWorkerShiftIsNotOpsCancelled?: boolean
    bypassWorkerW9FormRequired?: boolean
    bypassRecurringWorkerIsAvailable?: boolean
    bypassRecurringSlotAvailable?: boolean
    bypassI9FormRequired?: boolean
  }
}

export type ShiftAutoFillStepData = {
  minimumRankScore: number
  batchSize?: number
  shiftAutoFillType?: ShiftAutoFillType
  shiftAutoFillMode?: ShiftAutoFillMode
  bypasses?: GenericAcceptShiftBypasses
  onLimitReached?: ActionOnLimitReached[]
}

export type ShiftAutoFillStopSessionsStepData = Record<string, never>

export enum SlotsConfigType {
  ADD_TO_SLOTS_REQUESTED = 'ADD_TO_SLOTS_REQUESTED',
  COEFFICIENT_BASED = 'COEFFICIENT_BASED',
}

export const slotsConfigTypeMenuItems: Array<{
  value: SlotsConfigType
  label: string
}> = [
  {
    value: SlotsConfigType.ADD_TO_SLOTS_REQUESTED,
    label: 'Add to Slots Requested',
  },
  {
    value: SlotsConfigType.COEFFICIENT_BASED,
    label: '% of Slots Requested',
  },
] as const

export type SlotsConfig = {
  type: SlotsConfigType
  slotsToAddToRequested?: number
  coefficientForSlotsRequested?: number
}

export enum PayAmountType {
  FIXED = 'FIXED',
}

export type PayAmountConfig = {
  type: PayAmountType.FIXED
  payAmount: number
}

export type AnnouncementStepData = {
  message: string
  imageUrls?: string[]
  // legacy filter fields (kept for backwards compatibility)
  minTotalShifts?: number
  maxTotalShifts?: number
  minShiftsWithCompany?: number
  maxShiftsWithCompany?: number
  excludeRecentRecipients?: boolean
  // new unified filter (takes precedence over legacy fields when both are present)
  workerFilter?: WorkerStepFilter
}

export enum AiConversationalSMSQuestionConversationType {
  PRE_SHIFT = 'PRE_SHIFT',
}

export type AiConversationalSMSQuestion = {
  index: number
  questionType: AiConversationalSMSQuestionType | AiSmsReservedReadinessItemType
  customQuestion?: string
}

export enum AiSmsReservedReadinessItemType {
  CURRENT_SHIFT_ATTENDANCE = 'ATTENDANCE_CURRENT_SHIFT',
  NEXT_SHIFT_ATTENDANCE = 'ATTENDANCE_NEXT_SHIFT',
}

export enum AiConversationalSMSQuestionType {
  START_TIME = 'START_TIME',
  SHIFT_LOCATION = 'SHIFT_LOCATION',
  DRESS_CODE = 'DRESS_CODE',
  STRICT_ATTENDANCE_POLICY = 'STRICT_ATTENDANCE_POLICY',
  AVERAGE_ATTENDANCE_POLICY = 'AVERAGE_ATTENDANCE_POLICY',
  CUSTOM_QUESTION = 'CUSTOM_QUESTION',
  CANCELLATION_POLICY = 'CANCELLATION_POLICY',
}

export enum AiConversationalSMSConversationStyle {
  WALK_THROUGH = 'WALK_THROUGH',
  SINGLE_COMPREHENSIVE_REMINDER = 'SINGLE_COMPREHENSIVE_REMINDER',
}

export enum AiSmsStrictMessageType {
  ALL = 'ALL',
  DYNAMIC = 'DYNAMIC',
}

export type AiSmsStepData = {
  conversationType: AiConversationalSMSQuestionConversationType
  questions: AiConversationalSMSQuestion[]
  initialMessage?: string
  conversationStyle: AiConversationalSMSConversationStyle
  workerFilter?: WorkerStepFilter
  additionalContextForSystem?: string // additional context that will be used in system prompt
  additionalInformationForWorker?: string
  strictMessageType?: AiSmsStrictMessageType
  isStrictMessage?: boolean
}

export type PreShiftConfirmationStepData = {
  isPushNotification: boolean
  isForEarlyShift: boolean // earlyShift = shifts start within 2am - 8:30am
}

export type RobocallStepData = {
  isForEarlyShift: boolean // earlyShift = shifts start within 2am - 8:30am
  workerFilter?: WorkerStepFilter
}

export enum EmailTrigger {
  LIVE_OPS_AUTOMATION = 'LIVE_OPS_AUTOMATION',
  WORKERS_ON_SHIFT = 'WORKERS_ON_SHIFT',
}

export type EmailStepData = {
  template: string
  emailTrigger: EmailTrigger
  excludeConfirmations?: boolean
  excludeCancellations?: boolean
  onlyIncludeNewWorkers?: boolean
  emailsToCc?: string[]
  sendEmailToBusiness?: boolean
  sendSmsToBusiness?: boolean
  smsTemplate?: string
  // Extra phone numbers to text in addition to the supervisor, each its own
  // individual SMS (not a group message).
  additionalSmsPhoneNumbers?: string[]
}

export type LateCancellationCheckpointStepData = {
  template: string
  sendEmailToBusiness?: boolean
  emailsToCc?: string[]
  sendSmsToBusiness?: boolean
  smsTemplate?: string
  additionalSmsPhoneNumbers?: string[]
}
